import { Trans, useTranslation } from 'react-i18next'

interface Props {
  from: string
  to: string
  // 3-state: undefined = alle, false = personbil only, true = varebil only.
  varebil: boolean | undefined
  klasse: string[]
  merke: string[]
  aarMin: number | undefined
  aarMax: number | undefined
}

// Mirrors the AAR_MIN_FLOOR applied in index.tsx — kept in sync manually since
// it's a product rule, not a value passed down.
const AAR_MIN_FLOOR = 2000

/**
 * Plain-language summary of exactly which query produced the chart and grid,
 * so the user can trust what they're looking at. Every active filter — period,
 * vehicle type, klasse, merke/modell selection, år-range — is spelled out.
 */
export default function QueryExplanation({
  from,
  to,
  varebil,
  klasse,
  merke,
  aarMin,
  aarMax,
}: Props) {
  const { t } = useTranslation('bruktbil')
  const vehicleText =
    varebil === undefined
      ? t('merkeModell.queryExplanation.alleKjoretoy')
      : varebil
        ? t('merkeModell.queryExplanation.kunVarebil')
        : t('merkeModell.queryExplanation.kunPersonbil')

  const klasseText =
    klasse.length === 0
      ? t('merkeModell.queryExplanation.ingenKlasse')
      : t('merkeModell.queryExplanation.sammenlignetKlasser', { n: klasse.length })

  const selectionText =
    merke.length > 0
      ? t('merkeModell.queryExplanation.merker', { list: merke.join(', ') })
      : t('merkeModell.queryExplanation.ingenMerker')

  const effectiveAarMin = aarMin ?? AAR_MIN_FLOOR
  const aarText = t('merkeModell.queryExplanation.aar', {
    from: effectiveAarMin,
    to: aarMax ?? t('merkeModell.queryExplanation.naa'),
  })

  return (
    <div
      data-testid="query-explanation"
      className="card p-3 text-xs text-gray-600 dark:text-gray-300 space-y-1"
    >
      <p className="font-medium text-gray-700 dark:text-gray-200">
        {t('merkeModell.queryExplanation.title')}
      </p>
      <p>
        <Trans
          t={t}
          i18nKey="merkeModell.queryExplanation.periode"
          values={{ from, to, vehicle: vehicleText }}
          components={{ b: <strong /> }}
        />
      </p>
      <p>{klasseText}</p>
      <p>{selectionText}</p>
      <p>{aarText}</p>
      <p className="text-gray-500 dark:text-gray-400">
        {t('merkeModell.queryExplanation.snittalder')}
      </p>
    </div>
  )
}
